import { useContext, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  InnerWrapper,
  MainContainer,
  TitleAndActionBox,
  FilterNonIdeal,
} from "../../../../common";
import AppTableFilters from "../../../../common/AppTableFilters";
import { AppPagination, UBButton } from "../../../../../Libraries";
import { DoubleArrowIcon } from "../../../../../assets/icons/icon.jsx";
import { analytics, triggerLogEvent } from "../../../../../config/firebase";
import { buildFiltersConfig } from "../../../../../utils";
import moment from "moment";
import { formatNum } from "../../../../../oscar-pos-core/constants";
import useTransactionListing, { initialListingFilters } from "../../../../../hooks/useTransactionListing";
import useRechargeCustomers from "../../../../../hooks/useRechargeCustomers";
import useEmptyListingRedirect from "../../../../../hooks/useEmptyListingRedirect";
import AddCustomerModal from "../../../../common/Modals/AddCustomerModal";
import ContactRow from "../../TransactionListing/ContactRow";
import EasyloadTransactionRow from "./EasyloadTransactionRow";
import walletMainStyles from "../../WalletMain.module.css";
import styles from "../../TransactionListing/TransactionListing.module.css";
import { useWalletContext } from "../../../../../context/WalletContext";
import { handleApi } from "../../../../../apis";
import { UPDATE_CUSTOMER_NAME } from "../../../../../apis/configuration";
import { LanguageContext } from "../../../../../context/LanguageContext";

const RECHARGE_TXN_TYPES = ["recharge"];

const EasyloadTransactionListingPage = ({
  customerPhone = null,
  customerName = null,
  recentTransactions = null,
  rechargeContactsCacheRef = null,
}) => {
  const navigate = useNavigate();
  const [I18n] = useContext(LanguageContext);

  const VIEW_OPTIONS = [
    { id: "contact", label: I18n.contact, value: "contact" },
    { id: "transaction", label: I18n.transaction, value: "transaction" },
  ];

  const [addContactModal, setAddContactModal] = useState(false);
  const [addContactInitName, setAddContactInitName] = useState("");
  const [addContactCustomerId, setAddContactCustomerId] = useState(null);
  const [addContactPhone, setAddContactPhone] = useState("");
  const [contactSearchQ, setContactSearchQ] = useState("");
  const [isResetFilter, setIsResetFilter] = useState(false);
  const updateCustomerNameCancelToken = useRef();

  const isContactDetail = Boolean(customerPhone);

  const {
    searchQ,
    setSearchQ,
    filters,
    setFilters,
    transactions,
    hasNext,
    isLoading,
    isInitialLoading,
    isError,
    companyOptions,
    typeOptions,
    handleLoadMore,
    handleFilterChange,
    handleRemoveChip,
    resetAndFetch,
  } = useTransactionListing({ txnTypes: RECHARGE_TXN_TYPES, customerPhone, initialFilters: customerPhone ? { view: "transaction" } : null, seedData: recentTransactions });
  const { walletData } = useWalletContext();
  const availableTelcos = walletData?.available_telcos || [];

  // Contact detail has no vendor drill-down — it always shows that customer's transactions
  const isTransactionView = isContactDetail || filters.view === "transaction";
  // L1 contact view fetches from the dedicated recharge customers API
  const rechargeCustomers = useRechargeCustomers({
    search: contactSearchQ,
    enabled: !isContactDetail && !isTransactionView,
    cacheRef: !isContactDetail ? rechargeContactsCacheRef : null,
  });

  const filtersConfig = useMemo(() => {
    const [searchConfig] = buildFiltersConfig(["keywords"], [], null, null, I18n.search_by_name_or_phone, null, null, null, null, I18n);

    if (isContactDetail) {
      const base = buildFiltersConfig(["amount", "date"], [], null, null, I18n.search_by_name_or_phone, null, null, null, null, I18n);
      const result = [
        ...base,
        { id: "companies", label: I18n.company, type: "checkbox", options: companyOptions, disabled: !companyOptions.length, preserveCase: true },
        { id: "type", label: I18n.type, type: "checkbox", options: typeOptions, disabled: !typeOptions.length, preserveCase: true },
      ];
      return result;
    }

    const viewConfig = {
      id: "view",
      label: I18n.view,
      type: "sort",
      options: VIEW_OPTIONS,
    };

    if (!isTransactionView) {
      return [viewConfig, searchConfig];
    }

    const base = buildFiltersConfig(["date", "amount"], [], null, null, I18n.search_by_name_or_phone, null, null, null, null, I18n);
    const result = [
      viewConfig,
      ...base,
      { id: "companies", label: I18n.company, type: "checkbox", options: companyOptions, disabled: !companyOptions.length },
      { id: "type", label: I18n.type, type: "checkbox", options: typeOptions, disabled: !typeOptions.length },
      searchConfig,
    ];
    return result;
  }, [isTransactionView, isContactDetail, companyOptions, typeOptions, I18n]);

  const activeSearchQ = isTransactionView ? searchQ : contactSearchQ;

  const filterState = useMemo(() => ({
    view: filters.view,
    keywords: activeSearchQ,
    date: { startDate: filters.startDate ?? null, endDate: filters.endDate ?? null },
    amount: {
      min: filters.fromAmount != null && filters.fromAmount !== "" ? String(filters.fromAmount) : "",
      max: filters.toAmount != null && filters.toAmount !== "" ? String(filters.toAmount) : "",
    },
    companies: filters.companies || [],
    type: filters.txnSubTypes || [],
  }), [filters, activeSearchQ]);

  const appliedChips = useMemo(() => {
    const chips = [];
    if (isTransactionView && !isContactDetail) {
      chips.push({ id: "view", label: I18n.view, value: I18n.transaction });
    }
    if ((activeSearchQ || "").trim()) {
      chips.push({ id: "keywords", label: I18n.search, value: activeSearchQ.trim(), count: 1 });
    }
    if (isTransactionView) {
      if (filters.startDate || filters.endDate) {
        chips.push({
          id: "date",
          label: I18n.date,
          value: `${filters.startDate ? moment(filters.startDate).format("MMM DD") : I18n.start} - ${filters.endDate ? moment(filters.endDate).format("MMM DD") : I18n.end}`,
        });
      }
      if (filters.fromAmount || filters.toAmount) {
        chips.push({
          id: "amount",
          label: I18n.amount,
          value: `Rs. ${formatNum(filters.fromAmount || 0)} - ${filters.toAmount ? formatNum(filters.toAmount) : "∞"}`,
        });
      }
      if (filters.companies?.length) {
        chips.push({
          id: "companies",
          label: I18n.company,
          value: filters.companies.length > 1 ? `(${filters.companies.length})` : filters.companies[0],
          count: filters.companies.length,
        });
      }
      if (filters.txnSubTypes?.length) {
        chips.push({
          id: "type",
          label: I18n.type,
          value: filters.txnSubTypes.length > 1 ? `(${filters.txnSubTypes.length})` : filters.txnSubTypes[0],
          count: filters.txnSubTypes.length,
        });
      }
    }
    return chips;
  }, [filters, activeSearchQ, isTransactionView, isContactDetail, I18n]);

  const isAppliedFilter = appliedChips.length > 0 || (activeSearchQ || "").trim().length > 0;

  const handleClearAllFilters = () => {
    setSearchQ("");
    setContactSearchQ("");
    setFilters(initialListingFilters);
    resetAndFetch(initialListingFilters, "");
    setIsResetFilter(true);
  };

  const handleContactViewDetail = (contact) => {
    triggerLogEvent(analytics, "w_easyload_txn_contact_detail");
    navigate("/UCash/EasyloadTransactions", {
      state: {
        customerPhone: contact.customer_phone,
        customerName: contact.display_name,
        recentTransactions: contact.recent_transactions || null,
      },
    });
  };

  const handleAddContact = (item) => {
    setAddContactInitName(item.display_name || item.customer_name || "");
    setAddContactPhone(item.consumer_number || item.customer_phone || "");
    setAddContactCustomerId(null);
    setAddContactModal(true);
  };

  const handleContactRowAddContact = (item) => {
    setAddContactInitName(item.display_name || "");
    setAddContactPhone(item.customer_phone || "");
    setAddContactCustomerId(item.customer_id || null);
    setAddContactModal(true);
  };

  const handleRowClick = (item) => {
    if (item.id) {
      navigate(`/UCash/TransactionDetail/recharge/${item.id}`);
    }
  };

  const handleUpdateCustomerName = async ({ customer_id, customer_name }) => {
    if (updateCustomerNameCancelToken.current) {
      updateCustomerNameCancelToken.current.cancel("New request appears, canceling previous request.");
    }
    updateCustomerNameCancelToken.current = axios.CancelToken.source();

    await handleApi(
      UPDATE_CUSTOMER_NAME,
      { customer_id, customer_name },
      updateCustomerNameCancelToken.current,
      navigate,
    );
    rechargeCustomers.resetAndFetch(contactSearchQ);
  };

  const isContactListView = !isTransactionView;

  const currentIsInitialLoading = isContactListView ? rechargeCustomers.isInitialLoading : isInitialLoading;
  const currentIsLoading = isContactListView ? rechargeCustomers.isLoading : isLoading;
  const currentHasNext = isContactListView ? rechargeCustomers.hasNext : hasNext;
  const currentHandleLoadMore = isContactListView ? rechargeCustomers.handleLoadMore : handleLoadMore;
  const currentListEmpty = isContactListView
    ? rechargeCustomers.contacts.length === 0
    : transactions.length === 0;

  // Only redirect on a confirmed-empty fetch — not when the fetch itself failed
  // (which would otherwise look identical: not loading, list still empty).
  const currentIsError = isContactListView ? rechargeCustomers.isError : isError;
  useEmptyListingRedirect(
    !currentIsInitialLoading && currentListEmpty && !isAppliedFilter && !isContactDetail && !currentIsError,
    "/UCash/Easyload",
  );

  const pageTitle = isContactDetail ? (customerName && customerPhone) ? `${customerName} - ${customerPhone}` : customerPhone : I18n.easyload_packages;

  const handleRecharge = () => {
    triggerLogEvent(analytics, "w_easyload_txn_sell_easyload");
    let network;
    if (isContactDetail) {
      network = availableTelcos?.find(x => x.id.toLowerCase() === transactions[0].service.toLowerCase());
    }
    navigate("/UCash/Easyload", isContactDetail ? { state: { network, customerPhone, customerName } } : null)
  }
  const sellEasyloadBtn = (
    <UBButton
      id="easyload_sell_easyload_btn"
      title={I18n.sell_easyload}
      className="ubButtonStyle ubPrimaryButton textUppercase fontSize14"
      onClick={handleRecharge}
      disabled={isContactDetail && currentIsInitialLoading}
    />
  );

  return (
    <MainContainer fluid className="quickSalePage">
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: pageTitle,
              isBack: true,
              onBack: isContactDetail ? () => navigate(-1) : () => navigate("/UCash"),
              id: "easyload_txn_list",
              isTitleCase:false
            }}
          >
            <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {sellEasyloadBtn}
            </Box>
          </TitleAndActionBox>

          <InnerWrapper className="innerWrapper newInnerWrapper">
            <AppTableFilters
              filtersConfig={filtersConfig}
              filterState={filterState}
              isResetFilter={isResetFilter}
              setIsResetFilter={setIsResetFilter}
              onFilterChange={(key, value) => {
                triggerLogEvent(analytics, "w_easyload_txn_filter_applied");
                if (String(key).toLowerCase() === "keywords" && !isTransactionView) {
                  setContactSearchQ(value ?? "");
                  return;
                }
                handleFilterChange(key, value);
              }}
              appliedChips={appliedChips}
              onRemoveChip={(chip) => {
                triggerLogEvent(analytics, "w_easyload_txn_filter_removed");
                if (chip.id === "keywords" && !isTransactionView) {
                  setContactSearchQ("");
                  return;
                }
                handleRemoveChip(chip);
              }}
            >
              {!isTransactionView && (
                <UBButton
                  id="easyload_txn_view_transactions_btn"
                  title={I18n.transactions}
                  iconComponent={<DoubleArrowIcon width={12} height={12} />}
                  className={styles.viewTransactionsBtn}
                  onClick={() => handleFilterChange("view", "transaction")}
                />
              )}
            </AppTableFilters>

            <Box key={`${isTransactionView ? "txn" : "contact"}-${isContactDetail ? "detail" : "list"}`}>
              {isTransactionView ? (
                <Box className="invoiceListTableHeader accountTableHeaderMain">
                  <Box className={`invListTableHeaderCol ${isContactDetail ? "fourColumn" : "fiveColumn"} textLeft`}>
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.date}</span>
                  </Box>
                  <Box className={`invListTableHeaderCol ${isContactDetail ? "fourColumn" : "fiveColumn"} textLeft`}>
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.company}</span>
                  </Box>
                  <Box className={`invListTableHeaderCol ${isContactDetail ? "fourColumn" : "fiveColumn"} textLeft`}>
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.type}</span>
                  </Box>
                  {!isContactDetail && (
                    <Box className="invListTableHeaderCol fiveColumn textLeft">
                      <span className="fontSize12 fontWeight500 newLightColor">{I18n.contact}</span>
                    </Box>
                  )}
                  <Box className={`invListTableHeaderCol ${isContactDetail ? "fourColumn" : "fiveColumn"} textRight`}>
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.amount}</span>
                  </Box>
                </Box>
              ) : (
                <Box className="invoiceListTableHeader accountTableHeaderMain">
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.contact}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.phone_number}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.no_of_transactions}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn justifyCenter">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.actions}</span>
                  </Box>
                </Box>
              )}

              <Box className={`${styles.listWrapper} generic-scrollbar`} id="easyloadTxnListScrollable">
                {currentIsInitialLoading ? (
                  <Box className={`${walletMainStyles.transactionListLoadingBox} h-100`}>
                    <CircularProgress size={25} />
                  </Box>
                ) : currentListEmpty ? (
                  <Box className="h-100">
                    {isAppliedFilter ? (
                      <FilterNonIdeal
                        title={I18n.no_transactions_matching_those_filters}
                        subTitle={I18n.try_adjusting_your_filters_or}
                        subTitleText={I18n.reset_small}
                        subTitleTextOnClick={handleClearAllFilters}
                      />
                    ) : (
                      <FilterNonIdeal
                        title={I18n.no_transactions_found}
                        subTitle={I18n.transactions_will_appear_here_once_available}
                      />
                    )}
                  </Box>
                ) : (
                  <AppPagination
                    key={isTransactionView ? "txn-pager" : "contact-pager"}
                    data={{
                      handleNext: currentHandleLoadMore,
                      pageLoad: currentIsLoading,
                      count: currentHasNext,
                      list: isContactListView ? rechargeCustomers.contacts : transactions,
                      scrollableTarget: "easyloadTxnListScrollable",
                    }}
                  >
                    {isTransactionView
                      ? transactions.map((item, idx) => (
                        <EasyloadTransactionRow
                          key={item.id || idx}
                          item={item}
                          hideContact={isContactDetail}
                          onAddContact={handleAddContact}
                          onRowClick={handleRowClick}
                        />
                      ))
                      : rechargeCustomers.contacts.map((item, idx) => (
                        <ContactRow
                          key={item.customer_id || `${item.display_name}_${idx}`}
                          item={item}
                          onViewDetail={handleContactViewDetail}
                          onAddContact={handleContactRowAddContact}
                        />
                      ))}
                  </AppPagination>
                )}
              </Box>
            </Box>
          </InnerWrapper>
        </Grid>
      </Grid>

      <AddCustomerModal
        isOpen={addContactModal}
        handleClose={() => setAddContactModal(false)}
        initialName={addContactInitName}
        initialPhone={addContactPhone}
        type="in"
        title={I18n.contact.toLowerCase()}
        isTypeField={true}
        isOnlyPhoneList={true}
        onSaveCallback={() => setAddContactModal(false)}
        onRemoteSave={addContactCustomerId ? handleUpdateCustomerName : undefined}
        remoteCustomerId={addContactCustomerId}
      />
    </MainContainer>
  );
};

export default EasyloadTransactionListingPage;
